import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Row, Select, Table, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import fileDownload from 'js-file-download';
import startCase from 'lodash/startCase';

import styles from '../styles';
import { errorNotificationOnBlob, successNotification } from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import Breadcrumbs from '../Components/Breadcrumbs';
import { clearIssueChallans, getIssueChallans } from '../Actions';
import { currentFinancialYear, financialYears } from '../financial-years';

const ViewChallan = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const [Year, SetYear] = useState(currentFinancialYear);

  useEffect(() => {
    dispatch(getIssueChallans({ year: Year }));

    return () => {
      dispatch(clearIssueChallans());
    };
  }, [Year]);

  const issueChallans = useSelector(state => state.issueChallans);

  const onFileDownload = useCallback(async id => {
    try {
      const { data } = await Api.getFile(`/issuereceive/download/${id}`);
      fileDownload(data, `challan-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  }, []);

  const downloadColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="Download">
          <DownloadOutlined
            className={classes.tableIcon}
            onClick={() => onFileDownload(record.id)}
          />
        </Tooltip>
      ),
    []
  );

  const columns = useMemo(
    () => [
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Challan #',
        dataIndex: 'challanNo',
        sorter: sorter('challanNo'),
        onFilter: onFilter('challanNo'),
        filterDropdown,
        filterIcon,
        render: (_text, record) => startCase(record.challanNo),
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        sorter: dateSorter('createdAt'),
        onFilter: onDateFilter('createdAt'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: createdAt =>
          createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
      },
      {
        title: 'From',
        children: [
          {
            title: 'Factory',
            dataIndex: 'fromFactory',
            sorter: sorter('fromFactory'),
            onFilter: onFilter('fromFactory'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Department',
            dataIndex: 'fromDepartment',
            sorter: sorter('fromDepartment'),
            onFilter: onFilter('fromDepartment'),
            filterDropdown,
            filterIcon,
            render: (_text, record) => startCase(record.fromDepartment),
          },
          {
            title: 'Process',
            dataIndex: 'fromProcess',
            sorter: sorter('fromProcess'),
            onFilter: onFilter('fromProcess'),
            filterDropdown,
            filterIcon,
            render: (_text, record) => startCase(record.fromProcess),
          },
          {
            title: 'User',
            dataIndex: 'createdBy',
          },
        ],
      },
      {
        title: 'To',
        children: [
          {
            title: 'Factory',
            dataIndex: 'toFactory',
            sorter: sorter('toFactory'),
            onFilter: onFilter('toFactory'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Department',
            dataIndex: 'toDepartment',
            sorter: sorter('toDepartment'),
            onFilter: onFilter('toDepartment'),
            filterDropdown,
            filterIcon,
            render: (_text, record) => startCase(record.toDepartment),
          },
          {
            title: 'Process',
            dataIndex: 'toProcess',
            sorter: sorter('toDepartment'),
            onFilter: onFilter('toDepartment'),
            filterDropdown,
            filterIcon,
            render: (_text, record) => startCase(record.toProcess),
          },
          {
            title: 'User',
            dataIndex: 'toUser',
          },
        ],
      },
      {
        title: 'Download',
        render: downloadColumn,
        fixed: 'right',
        width: '6%',
      },
    ],
    []
  );

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1600, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Challan"
        moduleLink="/issuereceive/challan"
        subModuleName=""
      />
      <div className={classes.tableStyle}>
        <Row>
          <Select
            value={Year}
            style={{ width: 150 }}
            onChange={value => SetYear(value)}
          >
            {financialYears.map(el => (
              <Select.Option key={el} value={el}>
                {el}
              </Select.Option>
            ))}
          </Select>
        </Row>
        <br />
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={issueChallans}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

ViewChallan.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(ViewChallan);
